/**
 * UI-only speech helpers built on the Web Speech API.
 * Speech-to-text uses SpeechRecognition / webkitSpeechRecognition.
 * Text-to-speech uses speechSynthesis in the current browser tab.
 */

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0?: { transcript?: string };
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionErrorLike = {
  error?: string;
  message?: string;
};

type SpeechRecognitionInstance = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorLike) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

const TTS_KEEPALIVE_MS = 9000;
const TTS_CHUNK_CHARS = 220;

export function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

export function canUseSpeechToText(): boolean {
  return Boolean(getSpeechRecognitionCtor());
}

export function canUseTextToSpeech(): boolean {
  return typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';
}

export function unlockBrowserSpeech(): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    const utterance = new SpeechSynthesisUtterance(' ');
    utterance.volume = 0;
    window.speechSynthesis.speak(utterance);
    window.speechSynthesis.cancel();
  } catch {
    // Some browsers reject speech outside a gesture; ignore.
  }
}

export function speakableText(raw: string): string {
  return String(raw || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/[*_#~>|-]+/g, ' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

export function describeSpeechError(error?: string): string {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked. Allow the mic in your browser to talk to Ask AACore AI.';
    case 'no-speech':
      return 'I did not catch that. Tap the microphone and try again.';
    case 'audio-capture':
      return 'No microphone was found. Connect a mic and try again.';
    case 'network':
      return 'Voice input needs a network connection in this browser.';
    case 'aborted':
      return '';
    default:
      return error ? 'Voice input stopped. You can type or try the microphone again.' : '';
  }
}

function preferredVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en-US').toLowerCase();
  const langPrefix = lang.slice(0, 2);
  const score = (voice: SpeechSynthesisVoice) => {
    const voiceLang = (voice.lang || '').toLowerCase();
    const name = voice.name || '';
    let value = 0;
    if (voiceLang === lang) value += 8;
    else if (voiceLang.startsWith(langPrefix)) value += 6;
    else if (voiceLang.startsWith('en')) value += 3;
    if (voice.localService) value += 2;
    if (/female|zira|samantha|siri|aria|jenny|sara|susan|linda|hazel|eva|karen|moira|tessa|fiona|victoria/i.test(name)) {
      value += 12;
    }
    if (/google|natural|neural|premium/i.test(name)) value += 3;
    if (/\bmale\b|david|mark|george|fred|daniel|ravi|james/i.test(name)) value -= 10;
    return value;
  };
  return [...voices].sort((left, right) => score(right) - score(left))[0] || null;
}

function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  if (!canUseTextToSpeech()) return Promise.resolve([]);
  const existing = window.speechSynthesis.getVoices();
  if (existing.length) return Promise.resolve(existing);
  return new Promise((resolve) => {
    const finish = () => {
      window.speechSynthesis.removeEventListener('voiceschanged', finish);
      window.clearTimeout(timer);
      resolve(window.speechSynthesis.getVoices());
    };
    const timer = window.setTimeout(finish, 1500);
    window.speechSynthesis.addEventListener('voiceschanged', finish);
  });
}

function chunkForSpeech(text: string): string[] {
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const chunks: string[] = [];
  let current = '';
  sentences.forEach((sentence) => {
    const next = current ? `${current} ${sentence}` : sentence;
    if (next.length > TTS_CHUNK_CHARS && current) {
      chunks.push(current);
      current = sentence;
    } else {
      current = next;
    }
  });
  if (current) chunks.push(current);
  return chunks.length ? chunks : [text];
}

export type BrowserTtsHandle = {
  speak: (text: string) => Promise<void>;
  stop: () => void;
  speaking: () => boolean;
};

export function createBrowserTts(): BrowserTtsHandle {
  let cancelled = false;
  let keepAlive: number | null = null;
  let active = false;

  const clearKeepAlive = () => {
    if (keepAlive != null) {
      window.clearInterval(keepAlive);
      keepAlive = null;
    }
  };

  const stop = () => {
    cancelled = true;
    active = false;
    clearKeepAlive();
    if (canUseTextToSpeech()) {
      window.speechSynthesis.cancel();
    }
  };

  const speak = async (text: string) => {
    const spoken = speakableText(text);
    if (!spoken || !canUseTextToSpeech()) return;
    stop();
    cancelled = false;
    active = true;
    const voices = await loadVoices();
    if (cancelled) return;
    const voice = preferredVoice(voices);
    const chunks = chunkForSpeech(spoken);
    keepAlive = window.setInterval(() => {
      if (!window.speechSynthesis.speaking) return;
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }, TTS_KEEPALIVE_MS);

    try {
      for (const chunk of chunks) {
        if (cancelled) break;
        await new Promise<void>((resolve, reject) => {
          const utterance = new SpeechSynthesisUtterance(chunk);
          utterance.lang = voice?.lang || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
          if (voice) utterance.voice = voice;
          utterance.rate = 1;
          utterance.pitch = 1;
          utterance.onend = () => resolve();
          utterance.onerror = (event) => {
            if (event.error === 'canceled' || event.error === 'interrupted') {
              resolve();
              return;
            }
            reject(event);
          };
          window.speechSynthesis.speak(utterance);
        });
      }
    } finally {
      clearKeepAlive();
      active = window.speechSynthesis.speaking;
    }
  };

  return {
    speak,
    stop,
    speaking: () => active || (canUseTextToSpeech() && window.speechSynthesis.speaking),
  };
}

export type BrowserSttHandlers = {
  onTranscript: (text: string, isFinal: boolean) => void;
  onError?: (message: string) => void;
  onEnd?: () => void;
};

export function createBrowserStt(handlers: BrowserSttHandlers): { start: () => void; stop: () => void } | null {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) return null;

  let recognition: SpeechRecognitionInstance | null = null;

  const stop = () => {
    try {
      recognition?.stop();
    } catch {
      recognition?.abort();
    }
  };

  const start = () => {
    recognition?.abort();
    recognition = new Ctor();
    recognition.lang = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const piece = result?.[0]?.transcript || '';
        if (result.isFinal) finalText += piece;
        else interim += piece;
      }
      if (finalText.trim()) handlers.onTranscript(finalText.trim(), true);
      else if (interim.trim()) handlers.onTranscript(interim.trim(), false);
    };
    recognition.onerror = (event) => {
      const message = describeSpeechError(event.error);
      if (message) handlers.onError?.(message);
    };
    recognition.onend = () => {
      handlers.onEnd?.();
    };
    recognition.start();
  };

  return { start, stop };
}
